import { useEffect, useState } from "react";
import "./header.scss";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import fontawesome from "@fortawesome/fontawesome";
import {
  faLongArrowAltRight,
  faPhone,
  faEnvelope,
  faMapMarker,
} from "@fortawesome/fontawesome-free-solid";

import * as CONTACT_DETAILS from "content/site/contactDetails.json";
import * as CONFIG_DATA from "content/site/config.json";

import { SocialMedia } from "components/socialMedia/socialMedia";
import { Tab } from "components/Tab/tab";
import ButtonTile from "components/buttonTile/buttonTile";
import menuIcon from "../../images/menu.svg";
import closeIcon from "../../images/close.svg";
import { convertStringToValidURL, createValidImageURL } from "data/Utils";
import parseMD from "parse-md";
import { Service } from "data/DataTypes";

fontawesome.library.add(faLongArrowAltRight, faPhone, faEnvelope, faMapMarker);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [fileKeys, setFileKeys] = useState<Service[]>([]);

  const location = useLocation();

  const isHomePage = location.pathname === "/" || location.pathname === "/Home";

  /* =========================
     SCROLL HANDLING (CLEAN)
  ========================== */
  useEffect(() => {
    let isMounted = true;

    const markdownFiles = require.context(
      "content/services",
      false,
      /\.\/.*md/i
    );

    const loadServices = async () => {
      const services: Service[] = [];

      const tasks = markdownFiles.keys().map(async (markdownFile) => {
        const name = markdownFile.split(".", 2)[1] + ".md";
        const markdown = require("content/services" + name);

        try {
          const response = await fetch(markdown);
          const text = await response.text();

          const { metadata } = parseMD(text);

          const service: Service = {
            ...(metadata as Service),
            id: convertStringToValidURL(markdownFile),
          };

          services.push(service);
        } catch (e) {
          // optional logging
        }
      });

      await Promise.all(tasks);

      if (isMounted) {
        setFileKeys(services);
      }
    };

    loadServices();

    const onScroll = () => {
      setScrolled(window.scrollY > 80);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* =========================
     ROUTE CHANGE RESET
  ========================== */
  useEffect(() => {
    setShowMenu(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMenu = () => setShowMenu((v) => !v);
  const closeMenu = () => setShowMenu(false);

  const contactDetails = CONTACT_DETAILS;
  const config = CONFIG_DATA;

  return (
    <>
      {/* =========================
          TOP INFO BAR
      ========================== */}
      <div className="Header_topInfo">
        <div className="flex justify-center items-center space-x-6">
          <FontAwesomeIcon
            className="Header_contactDetailsIcon"
            icon={faPhone as IconProp}
          />
          <h6>{contactDetails.phoneNumber}</h6>

          <FontAwesomeIcon
            className="Header_contactDetailsIcon"
            icon={faEnvelope as IconProp}
          />
          <h6>{contactDetails.email}</h6>

          <FontAwesomeIcon
            className="Header_contactDetailsIcon"
            icon={faMapMarker as IconProp}
          />
          <h6>{contactDetails.address}</h6>

          <SocialMedia
            rootclassname="Header_contentDetailsSocialMedia"
            modifiers="smallIcon"
          />
        </div>
      </div>

      {/* =========================
          HEADER (STICKY ROOT)
      ========================== */}
      <div className="Header_container">
        <section
          className={classNames("Header_root", {
            homepage: isHomePage,
          })}
        >
          <div
            className={classNames("Header_content", {
              scrolled,
            })}
          >
            {/* =========================
                DESKTOP CONTENT
            ========================== */}
            <div className="Header_contentInner">
              <Link className="alt items-center" to="/">
                <div className="flex flex-row items-center">
                  <img
                    className="Header_logo"
                    src={createValidImageURL(config.logo)}
                    alt="Clifton Technology"
                  />

                  <div>
                    <h3>Clifton Technology</h3>
                    <h5 className="text-secondAccent">
                      Design <span className="text-blackColor">•</span>{" "}
                      Development <span className="text-blackColor">•</span>{" "}
                      Testing
                    </h5>
                  </div>
                </div>
              </Link>

              <div className="Header_menu">
                {fileKeys
                  .filter((s) => s.showOnNavigationBar)
                  .map((service) => (
                    <Tab
                      key={service.id}
                      label={service.title}
                      to={`/services/${service.id}`}
                      modifiers={scrolled ? "scrolled" : ""}
                    />
                  ))}
                <Tab
                  label="Home"
                  to="/Home"
                  altTos={["/"]}
                  modifiers={scrolled ? "scrolled" : ""}
                />
                <Tab
                  label="Contact Us"
                  to="/Contact"
                  modifiers={classNames("special", { scrolled })}
                  children={
                    <FontAwesomeIcon
                      className="Header_arrowIcon"
                      icon={faLongArrowAltRight as IconProp}
                    />
                  }
                />
              </div>
            </div>

            {/* =========================
                MOBILE HEADER BAR
            ========================== */}
            <div className="Header_mobile">
              <div className="flex items-center gap-3">
                <img
                  className="Header_logo"
                  src={createValidImageURL(config.logo)}
                  alt="Clifton Technology"
                />

                <h3>Clifton Technology</h3>
              </div>

              <ButtonTile
                rootclassname={"alt"}
                icon={showMenu ? closeIcon : menuIcon}
                onSelect={toggleMenu}
              />
            </div>
          </div>
        </section>

        {/* =========================
            MOBILE OVERLAY MENU
        ========================== */}
        <div
          className={classNames("Header_mobileView", {
            open: showMenu,
          })}
        >
          <div className="Header_mobileViewInner">
            <div className="Header_mobileMenu">
              <Tab label="Home" to="/Home" onSelect={closeMenu} />
              <Tab label="Services" to="/Services" onSelect={closeMenu} />
              <Tab label="Contact Us" to="/Contact" onSelect={closeMenu} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
